import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Edge,
  Node,
  useEdgesState,
  useNodesState,
  Viewport,
  XYPosition,
} from 'reactflow';

import { useLiveRegion } from '@dbt-labs/sourdough';

import { nodePositionComparator } from '../dag/calculateDagreLayout';
import { NodeType } from '../dag/dagConstants';
import { DagStats, DbtDagNode } from '../dag/DagInterfaces';
import { InstrumentedDagreResult } from '../dag/DagreTypes';
import { convertFromDagreToReact, DagGraph } from '../dag/internalDag';
import { useDagContext } from '../providers/DagProvider';
import { useViewportContext } from '../providers/ViewportProvider';
import { getHighlightedNodesAndEdges } from './highlights';

interface RenderGraphProps<TDbtDagNode extends DbtDagNode> {
  dagContainerEl: HTMLDivElement | null | undefined;
  dagreResult: InstrumentedDagreResult<TDbtDagNode> | null;
  primaryNodeIds: string[] | undefined;
  dagGraph: DagGraph<TDbtDagNode> | undefined;
  forceRenderComplexDag: boolean;
  onDagRenderStart?: (stats: Omit<DagStats, 'renderDurationMillis'>) => void;
  onRenderComplete?: (stats: DagStats) => void;
}

function getLiveRegionMessage(primaryNodeIds?: string[], otherNodes?: number) {
  if (primaryNodeIds?.length && otherNodes) {
    return `DAG showing ${primaryNodeIds?.join(', ')} and ${otherNodes} other nodes`;
  } else if (primaryNodeIds?.length) {
    return `DAG showing ${primaryNodeIds?.join(', ')}`;
  } else if (otherNodes) {
    return `DAG showing ${otherNodes} nodes`;
  }
}

interface ViewportBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

function getViewportBounds(
  viewport: Viewport,
  containerWidth: number,
  containerHeight: number,
): ViewportBounds {
  const { x, y, zoom } = viewport;
  // Convert screen coordinates to flow coordinates
  // viewport.x and viewport.y represent the translation of the flow
  // To get flow coordinates: flowX = (screenX - viewport.x) / zoom
  return {
    minX: -x / zoom,
    maxX: (containerWidth - x) / zoom,
    minY: -y / zoom,
    maxY: (containerHeight - y) / zoom,
  };
}

function isNodeInViewport(
  nodePosition: XYPosition,
  nodeWidth: number,
  nodeHeight: number,
  bounds: ViewportBounds,
): boolean {
  // Check if node rectangle intersects with viewport rectangle
  const nodeRight = nodePosition.x + nodeWidth;
  const nodeBottom = nodePosition.y + nodeHeight;

  return (
    nodePosition.x < bounds.maxX &&
    nodeRight > bounds.minX &&
    nodePosition.y < bounds.maxY &&
    nodeBottom > bounds.minY
  );
}

export const useRenderGraph = <TDbtDagNode extends DbtDagNode>({
  dagContainerEl,
  dagreResult,
  primaryNodeIds,
  dagGraph,
  onDagRenderStart,
  onRenderComplete,
  forceRenderComplexDag,
}: RenderGraphProps<TDbtDagNode>) => {
  const viewport = useViewportContext();
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;

  const dagContext = useDagContext();

  const { LiveRegion, setMessage: setLiveRegionMessage } = useLiveRegion({
    display: 'sr-only',
    testId: 'dag-live-region',
  });

  // The rendered Nodes and Edges
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState<any>([]);

  // Store all edges for viewport filtering (unfiltered set)
  const allEdgesRef = useRef<Edge[]>([]);

  // UniqueIds which represents the highlighted node
  const [highlightedNodeIds, setHighlightedNodeIds] = useState<string[] | undefined>(
    undefined,
  );

  // If non-null, it means that we are about to track the duration of rendering the DAG
  const [renderEventData, setRenderEventData] = useState<Omit<
    DagStats,
    'renderDurationMillis'
  > | null>(null);

  const [lastDagreResult, setLastDagreResult] = useState<string>();

  const renderGraph = useCallback(
    (dagreResult: InstrumentedDagreResult<TDbtDagNode>, forceRender?: boolean) => {
      if (!dagGraph || !dagreResult) return;
      const lastRequestSerialized = JSON.stringify({
        nodes: dagreResult.nodes,
        edges: dagreResult.edges,
      });
      if (!forceRender && lastRequestSerialized === lastDagreResult) return;
      setLastDagreResult(lastRequestSerialized);
      const { reactFlowNodes, reactFlowEdges } = convertFromDagreToReact(dagreResult);
      const posMap: Record<string, XYPosition> = {};
      reactFlowNodes.forEach(({ id, position }) => {
        posMap[id] = position;
      });
      const result = getHighlightedNodesAndEdges({
        uniqueIds: primaryNodeIds,
        nodes: reactFlowNodes,
        edges: reactFlowEdges,
        dagGraph,
      }) || { nodes: reactFlowNodes, edges: reactFlowEdges };

      // Store all edges for viewport filtering
      allEdgesRef.current = result.edges;

      setNodes(result.nodes);
      setEdges(result.edges);
      const eventData = {
        requestId: dagreResult.dagRequestId,
        nodeCount: reactFlowNodes.length,
        edgeCount: reactFlowEdges.length,
        layoutDurationMillis: dagreResult.wasCached
          ? null
          : dagreResult.layoutDurationMillis,
        renderStartTimestamp: dagreResult.endLayoutTimestamp || Date.now(),
        forceRenderComplexDag,
      };
      onDagRenderStart && onDagRenderStart(eventData);
      setRenderEventData(eventData.nodeCount ? eventData : null);
    },
    [
      dagGraph,
      lastDagreResult,
      setEdges,
      primaryNodeIds,
      setNodes,
      forceRenderComplexDag,
      onDagRenderStart,
    ],
  );

  const renderGraphRef = useRef(renderGraph);
  renderGraphRef.current = renderGraph;

  useEffect(() => {
    if (!dagreResult) return;
    renderGraphRef.current(dagreResult);
  }, [dagreResult]);

  useEffect(() => {
    const otherNodes =
      Object.keys(dagGraph || {}).length - (primaryNodeIds?.length || 0);
    const message = getLiveRegionMessage(primaryNodeIds, otherNodes);
    if (message) {
      setLiveRegionMessage(message);
    }
  }, [dagGraph, primaryNodeIds, setLiveRegionMessage]);

  useEffect(() => {
    if (!renderEventData) return;
    const renderDurationMillis = Date.now() - renderEventData?.renderStartTimestamp;

    onRenderComplete?.({
      ...renderEventData,
      renderDurationMillis,
    });
    setRenderEventData(null);
  }, [onRenderComplete, renderEventData]);

  let boundingBox = { x: 0, y: 0, width: 0, height: 0 };
  if (dagContainerEl) {
    const { width, height, top, left } = dagContainerEl.getBoundingClientRect();
    boundingBox = { x: left, y: top, width, height };
  }
  const boundingBoxRef = useRef(boundingBox);
  boundingBoxRef.current = boundingBox;

  // Sort nodes once (stable unless nodes change)
  const sortedNodes = useMemo(() => {
    const sorted = [...(nodes || [])];
    sorted.sort?.((a, b) => nodePositionComparator(a.position, b.position));
    return sorted;
  }, [nodes]);

  // Fallback node dimensions if style dimensions are unavailable
  const DEFAULT_NODE_WIDTH = 360;
  const DEFAULT_NODE_HEIGHT = 140;

  const updateVisibleEdges = useCallback(
    (viewport: Viewport, containerWidth: number, containerHeight: number) => {
      if (allEdgesRef.current.length === 0) return;

      const bounds = getViewportBounds(viewport, containerWidth, containerHeight);

      // Find all nodes that are visible in the viewport
      const visibleNodeIds = new Set<string>();
      for (const node of sortedNodes) {
        const nodeWidth = Number(node.style?.width) || DEFAULT_NODE_WIDTH;
        const nodeHeight = Number(node.style?.height) || DEFAULT_NODE_HEIGHT;
        if (isNodeInViewport(node.position, nodeWidth, nodeHeight, bounds)) {
          visibleNodeIds.add(node.id);
        }
      }

      // Filter edges to those with at least one visible node
      const visibleEdges = allEdgesRef.current.filter((edge) => {
        return visibleNodeIds.has(edge.source) || visibleNodeIds.has(edge.target);
      });

      setEdges(visibleEdges);
    },
    [sortedNodes, setEdges],
  );

  return useMemo(() => {
    return {
      LiveRegion,
      renderGraph,
      reactFlowProps: {
        nodes: sortedNodes,
        onNodesChange,
        edges,
        onEdgesChange,
      },
      updateVisibleEdges,
      resetNodes: () => {
        if (!dagreResult) return;
        renderGraph(dagreResult, true);
      },
      setNodes,
      setEdges,
      highlightedNodeIds,
      setHighlightedNodeIds,
      updateHighlightedNode: (selected: string[] | undefined) => {
        if (!dagGraph) return;
        dagContext.hideContextMenu();
        setHighlightedNodeIds(selected);
        const result = getHighlightedNodesAndEdges({
          uniqueIds: selected,
          dagGraph,
          nodes: sortedNodes as Node<any, NodeType>[],
          edges: allEdgesRef.current.length > 0 ? allEdgesRef.current : edges,
        }) || {
          nodes: sortedNodes,
          edges: allEdgesRef.current.length > 0 ? allEdgesRef.current : edges,
        };
        // Update all edges reference
        allEdgesRef.current = result.edges;
        setNodes(result.nodes);
        setEdges(result.edges);
      },
      boundingBox: boundingBoxRef.current,
    };
  }, [
    LiveRegion,
    dagContext,
    dagGraph,
    dagreResult,
    edges,
    highlightedNodeIds,
    sortedNodes,
    onEdgesChange,
    onNodesChange,
    renderGraph,
    setEdges,
    setNodes,
    updateVisibleEdges,
  ]);
};
